/**
 * Rêve Workspace sign in — RW-2564.
 *
 * The split-screen frame from `Rêve OS · Login.html`: photography on the left
 * at 54% under the brand guide's 30% black protection overlay, the real
 * tricolor lockup placed (not typed), a white form pane on the right, and one
 * Google button. Replaces the 360px white card on a noir field.
 *
 * Seven states live here, all the same frame — only the right pane changes, so
 * there is no layout jump between "checking your session" and "sign in":
 *
 *   AuthChecking      the session check on first paint          (App: `loading`)
 *   AuthSignIn        default sign in                           (App: `!user`)
 *   AuthRejected      authenticated with Google, wrong domain   (authError.kind 'domain')
 *   AuthPopupFailed   popup blocked or closed early             (authError.kind 'popup')
 *   AuthHandoff       popup returned, the app is taking over    (App: `signedInName`)
 *   AuthProvisioning  roles not written yet, the RW-702 window  (App: `roleLoaded && !userRole`)
 *   AuthSignedOut     sign-out confirmation                     (App: `signedOutAt`)
 *
 * The design's eighth state, the invited new hire landing from an invitation
 * link, is deliberately absent: nothing in the app issues such a link yet. It
 * belongs with RW-784, which builds the mechanism that would reach it.
 *
 * Type scale is the canvas's: 30px title, 14px body, 12px meta, 48px button,
 * everything on the pane inside a 380px column. Buttons are 2px per the brand
 * guide. The Google button follows Google's own branding rules — their mark on
 * a white ground, never on a brand fill — which also keeps the one coral
 * accent from competing with the coral in the logotype.
 */
import React from 'react';
import { colors, fonts, radius } from './theme';

/* One frame per office city, on rotation. The brand guide asks for
   Louisiana-specific rooms; only the New Orleans office is shot, so the
   rotation has one entry until Baton Rouge and the Northshore are
   photographed. Day-indexed rather than random so the frame is stable for the
   whole of a visit — and for every tab of it. */
export const HERO_ROTATION = [
  {
    city: 'New Orleans',
    src: '/login/hero-new-orleans.jpg',
    srcSmall: '/login/hero-new-orleans-960.jpg',
    alt: 'The Rêve Realtors office in New Orleans',
  },
];

const heroForToday = () => HERO_ROTATION[Math.floor(Date.now() / 86400000) % HERO_ROTATION.length];

const LOCKUP = '/login/reve-tricolor-white-text.png';
const OFFICES = 'Rêve Realtors · New Orleans · Baton Rouge · Northshore';
const CONNECT_HOST = 'connect.reverealtors.com';
const SUPPORT_EMAIL = 'support@reverealtors.com';

/* ── The photographic half ────────────────────────────────────────────────
   30% black protection overlay per the brand guide, the lockup top-left, one
   editorial line bottom-left. `compact` is the 320px mobile band. The photo is
   an <img> rather than a CSS background so phones can fetch the 960px file
   instead of the 2000px one. objectPosition favours the left of the frame:
   centred, a 16:9 room in a portrait pane crops to mostly ceiling. */
export const AuthPhoto = ({
  compact = false,
  line = 'Independent. Rooted here. Connected everywhere.',
  sub = 'One place for your numbers, your week, and the people you sell with.',
}) => {
  const hero = heroForToday();
  return (
    <div style={{
      position: 'relative',
      background: colors.noir,
      flex: compact ? 'none' : '0 0 54%',
      height: compact ? 320 : 'auto',
      minWidth: 0,
      overflow: 'hidden',
    }}
    >
      <img
        src={hero.src}
        srcSet={`${hero.srcSmall} 960w, ${hero.src} 2000w`}
        sizes={compact ? '100vw' : '54vw'}
        alt={hero.alt}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: compact ? 'center 62%' : '30% center' }}
      />
      {/* The brand guide's flat 30% black protection overlay. */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.30)' }} />
      {/* And a vignette under the two pieces of type, because 30% flat is not
          enough on THIS photograph: the lockup sits on a pale plaster ceiling
          and the editorial line on a light oak desk, and white-on-light at 30%
          is unreadable. The flat overlay above is what the guide specifies;
          this is what makes the type on top of it legible, and it has to
          survive any photo the rotation grows to hold. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0) 26%, rgba(0,0,0,0) 46%, rgba(0,0,0,0.58) 100%)',
        }}
      />
      <div style={{ position: 'absolute', left: compact ? 24 : 48, top: compact ? 26 : 44 }}>
        <img src={LOCKUP} alt="Rêve Realtors" style={{ height: compact ? 30 : 42, display: 'block' }} />
      </div>
      <div style={{ position: 'absolute', left: compact ? 24 : 48, bottom: compact ? 24 : 48, right: compact ? 24 : 48, color: colors.white }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.85, marginBottom: compact ? 8 : 14 }}>Rêve Workspace</div>
        <div style={{ fontFamily: fonts.display, fontSize: compact ? 25 : 44, lineHeight: 1.08, letterSpacing: '-0.01em', maxWidth: '20ch', marginBottom: compact ? 0 : 14 }}>{line}</div>
        {!compact && <div style={{ fontSize: 14, opacity: 0.85, maxWidth: 440, lineHeight: 1.55 }}>{sub}</div>}
      </div>
    </div>
  );
};

/* ── Pane primitives ──────────────────────────────────────────────────── */

const Eyebrow = ({ children, color = colors.gray2, style }) => (
  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color, ...style }}>{children}</div>
);

const Title = ({ children, size = 30, style }) => (
  <h1 style={{ fontFamily: fonts.heading, fontSize: size, fontWeight: 600, color: colors.noir, margin: '0 0 8px', letterSpacing: '-0.01em', lineHeight: 1.15, ...style }}>{children}</h1>
);

const Body = ({ children, style }) => (
  <p style={{ fontSize: 14, color: colors.textSoft, margin: '0 0 28px', lineHeight: 1.55, maxWidth: '46ch', ...style }}>{children}</p>
);

const Meta = ({ children, style }) => (
  <div style={{ marginTop: 14, fontSize: 12, color: colors.gray1, ...style }}>{children}</div>
);

const Aside = ({ children }) => (
  <div style={{ marginTop: 16, fontSize: 12.5, color: colors.gray2, lineHeight: 1.55 }}>{children}</div>
);

const MailLink = ({ children, subject, body }) => (
  <a
    href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ''}`}
    style={{ fontFamily: fonts.body, fontSize: 'inherit', fontWeight: 600, color: colors.coralInk }}
  >
    {children}
  </a>
);

const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.9a5 5 0 0 1-2.2 3.3v2.7h3.5c2.1-1.9 3.3-4.7 3.3-7.9z" />
    <path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.7l-3.5-2.7c-1 .7-2.2 1-3.7 1-2.8 0-5.2-1.9-6.1-4.5H2.3v2.8A10.9 10.9 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.9 14.1a6.6 6.6 0 0 1 0-4.2V7.1H2.3a10.9 10.9 0 0 0 0 9.8l3.6-2.8z" />
    <path fill="#EA4335" d="M12 5.4c1.6 0 3 .5 4.1 1.6l3.1-3.1A10.9 10.9 0 0 0 2.3 7.1l3.6 2.8C6.8 7.3 9.2 5.4 12 5.4z" />
  </svg>
);

const Spinner = ({ size = 16, color = colors.coral }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className="reve-auth-spin" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(15,16,17,0.12)" strokeWidth="2.5" />
    <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const Check = () => (
  <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: colors.moss, display: 'grid', placeItems: 'center' }}>
    <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1.5 6.2l3 3L10.5 2.5" fill="none" stroke={colors.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
  </span>
);

/* Google's sign-in button per their branding rules: white ground, their mark,
   our type, 2px radius so it still reads as ours. */
const GoogleButton = ({ label = 'Continue with Google', onClick, busy = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    aria-busy={busy || undefined}
    style={{
      width: '100%', height: 48, border: `1px solid ${colors.authLineStrong}`, borderRadius: radius.xs,
      background: colors.white, fontFamily: fonts.body, fontSize: 14, fontWeight: 600,
      color: busy ? colors.gray2 : colors.noir, display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 10, cursor: busy ? 'default' : 'pointer',
    }}
  >
    {busy ? <Spinner /> : <GoogleG />}{busy ? 'Opening Google' : label}
  </button>
);

/* Inline notice. `kind`: 'error' names something that was refused, 'quiet'
   explains something that simply did not finish. The error pair is this
   surface's own warm one (colors.authErr/authErrSoft), not `tone.err` — see
   the note on those tokens in theme.js. */
const Notice = ({ kind = 'error', children }) => (
  <div style={{
    display: 'flex', gap: 10, padding: '13px 14px', borderRadius: radius.xs,
    background: kind === 'error' ? colors.authErrSoft : colors.pearlLight,
    borderLeft: `2px solid ${kind === 'error' ? colors.authErr : colors.coral}`,
    marginBottom: 22,
  }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={kind === 'error' ? colors.authErr : colors.coralDark} strokeWidth="1.75" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
    </svg>
    <div style={{ fontSize: 13, lineHeight: 1.5, color: colors.text }}>{children}</div>
  </div>
);

const ConnectLine = () => (
  <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${colors.authLineFaint}`, fontSize: 12.5, color: colors.gray2, lineHeight: 1.55 }}>
    {'Not a Rêve agent? Connect members sign in at '}
    <a href={`https://${CONNECT_HOST}`} style={{ color: colors.coralInk, fontWeight: 600 }}>{CONNECT_HOST}</a>.
  </div>
);

/* ── The frame ────────────────────────────────────────────────────────────
   `.reve-auth-shell` owns the viewport floor: the login screen renders above
   the app shell, so `--reve-page-min-height` (published by `.reve-main`) does
   not exist here and this surface needs its own — and `100dvh` needs a
   stylesheet for its `@supports` fallback (RW-2552/RW-2561). */
const Frame = ({ mobile = false, children }) => (
  <div
    className="reve-auth-shell"
    style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', fontFamily: fonts.body, background: colors.white }}
  >
    {children}
  </div>
);

/* The form half. A 380px column, vertically centred, with the office line
   pinned bottom-right on the desktop frame. */
const Pane = ({ mobile = false, footer = true, children }) => {
  if (mobile) {
    return (
      <div style={{ flex: '1 0 auto', width: '100%', minWidth: 0, padding: '34px 24px 26px', display: 'flex', flexDirection: 'column', background: colors.white }}>
        {children}
        <div style={{ marginTop: 'auto', paddingTop: 22, fontSize: 11.5, color: colors.gray1, lineHeight: 1.5 }}>
          {footer ? <>{`Connect members sign in at ${CONNECT_HOST}`}<br /></> : null}
          {OFFICES}
        </div>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 72px', minWidth: 0, background: colors.white }}>
      <div style={{ maxWidth: 380, width: '100%', margin: '0 auto' }}>{children}</div>
      <div style={{ position: 'absolute', left: 0, right: 40, bottom: 26, textAlign: 'right', fontSize: 10.5, color: colors.gray1, letterSpacing: '0.04em' }}>{OFFICES}</div>
    </div>
  );
};

/* ── The states ───────────────────────────────────────────────────────── */

/* 1 · Default sign in. */
export const AuthSignIn = ({ onGoogle, busy = false, mobile = false }) => (
  <Frame mobile={mobile}>
    <AuthPhoto compact={mobile} />
    <Pane mobile={mobile}>
      <Eyebrow style={{ marginBottom: mobile ? 12 : 14 }}>Sign in</Eyebrow>
      <Title size={mobile ? 26 : 30}>Welcome back.</Title>
      <Body style={mobile ? { marginBottom: 24 } : undefined}>Sign in with your reverealtors.com Google account.</Body>
      <GoogleButton onClick={onGoogle} busy={busy} />
      <Meta>Access is limited to reverealtors.com accounts.</Meta>
      {!mobile && <ConnectLine />}
    </Pane>
  </Frame>
);

/* 2 · Wrong domain. The account authenticated with Google but is not ours. */
export const AuthRejected = ({ onRetry, email, mobile = false }) => (
  <Frame mobile={mobile}>
    <AuthPhoto compact={mobile} />
    <Pane mobile={mobile}>
      <Eyebrow style={{ marginBottom: mobile ? 12 : 14 }}>Sign in</Eyebrow>
      <Title size={mobile ? 26 : 30}>That account is not on Rêve.</Title>
      <Body style={{ marginBottom: mobile ? 18 : 22 }}>Workspace is for Rêve agents and staff.</Body>
      <Notice>
        {email ? <strong style={{ fontWeight: 600 }}>{email}</strong> : 'That address'}
        {' is not a reverealtors.com address, so it cannot open Workspace.'}
      </Notice>
      <GoogleButton label="Try a different account" onClick={onRetry} />
      {!mobile && (
        <>
          <Aside>
            {'Joining Rêve this month? Your invitation comes from Tammy. '}
            <MailLink subject="Resend my Workspace invitation" body={`The Workspace invitation for ${email || 'my account'} has not arrived.`}>Ask her to resend it.</MailLink>
          </Aside>
          <ConnectLine />
        </>
      )}
    </Pane>
  </Frame>
);

/* 3 · Popup blocked or closed early. Nothing about the account changed. */
export const AuthPopupFailed = ({ onRetry, code, mobile = false }) => (
  <Frame mobile={mobile}>
    <AuthPhoto compact={mobile} />
    <Pane mobile={mobile}>
      <Eyebrow style={{ marginBottom: mobile ? 12 : 14 }}>Sign in</Eyebrow>
      <Title size={mobile ? 26 : 30}>The Google window closed too early.</Title>
      <Body style={{ marginBottom: mobile ? 18 : 22 }}>Sign-in did not finish, so nothing changed on your account.</Body>
      <Notice kind="quiet">
        Usually a blocked popup. Allow popups for workspace.reverealtors.com, then try again. On an iPhone, open Workspace in Safari rather than inside another app.
      </Notice>
      <GoogleButton label="Try again" onClick={onRetry} />
      {!mobile && (
        <Aside>
          {'Still stuck after two tries? '}
          <MailLink
            subject="Workspace sign-in fails"
            body={`Sign-in does not finish on this device.\n\nError code: ${code || 'unknown'}\nBrowser: ${typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent}`}
          >
            Send the error to support.
          </MailLink>
          {' It arrives with your browser and the error code attached.'}
        </Aside>
      )}
    </Pane>
  </Frame>
);

/* 4 · The session check on first paint. Same frame as sign in, so the only
   thing that changes when it resolves is the content of the right pane. */
export const AuthChecking = ({ mobile = false }) => (
  <Frame mobile={mobile}>
    <AuthPhoto compact={mobile} />
    <Pane mobile={mobile} footer={false}>
      <div style={{ width: 200, maxWidth: '100%', height: 2, background: colors.authLineFaint, overflow: 'hidden', marginBottom: 22 }}>
        <div className="reve-auth-bar" style={{ width: '100%', height: '100%', background: colors.coral }} />
      </div>
      <Eyebrow style={{ marginBottom: 12 }}>Rêve Workspace</Eyebrow>
      <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>Checking your session.</div>
    </Pane>
  </Frame>
);

/* 5 · The provisioning gap (RW-702): authenticated, roles not written yet.
   `rolesApplied` is the real callable resolving, not a timer. */
export const AuthProvisioning = ({ name, rolesApplied = false, onReload, mobile = false }) => {
  const steps = [
    ['Account created', true],
    ['Roles applied', rolesApplied],
    ['Office and licence details', false],
  ];
  return (
    <Frame mobile={mobile}>
      <AuthPhoto compact={mobile} />
      <Pane mobile={mobile} footer={false}>
        <Eyebrow style={{ marginBottom: mobile ? 12 : 14 }}>First sign in</Eyebrow>
        <Title size={mobile ? 26 : 30}>{name ? `Setting up your account, ${name}.` : 'Setting up your account.'}</Title>
        <Body style={{ marginBottom: 24 }}>This happens once. Keep this window open.</Body>
        {/* The tick and the spinner are the only thing that says which step is
            where, and both are decorative SVG — so each row carries its state
            in its accessible name. A screen reader hears the progress the
            sighted reader sees, rather than three bare labels. */}
        <ul style={{ display: 'grid', gap: 12, margin: '0 0 26px', padding: 0, listStyle: 'none' }}>
          {steps.map(([label, done]) => (
            <li
              key={label}
              aria-label={`${label} — ${done ? 'done' : 'in progress'}`}
              style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: done ? colors.text : colors.gray2 }}
            >
              {done ? <Check /> : <Spinner />}{label}
            </li>
          ))}
        </ul>
        <div style={{ fontSize: 12.5, color: colors.gray2, lineHeight: 1.55 }}>
          {'Longer than a minute? '}
          <button type="button" onClick={onReload} style={{ background: 'none', border: 'none', padding: 0, fontFamily: fonts.body, fontSize: 12.5, fontWeight: 600, color: colors.coralInk, cursor: 'pointer' }}>Reload</button>
          {' or message Tammy. Nothing is lost either way.'}
        </div>
      </Pane>
    </Frame>
  );
};

/* 6 · Sign-out confirmation: say what happened before offering the button. */
export const AuthSignedOut = ({ onGoogle, at, busy = false, mobile = false }) => (
  <Frame mobile={mobile}>
    <AuthPhoto compact={mobile} />
    <Pane mobile={mobile}>
      <Eyebrow style={{ marginBottom: mobile ? 12 : 14 }}>Signed out</Eyebrow>
      <Title size={mobile ? 26 : 30}>You are signed out.</Title>
      <Body>This device is closed out of Workspace. Your Google account stays signed in elsewhere.</Body>
      <GoogleButton label="Sign back in" onClick={onGoogle} busy={busy} />
      {at ? <Meta>{`Signed out at ${at} on this device.`}</Meta> : null}
      {!mobile && <ConnectLine />}
    </Pane>
  </Frame>
);

/* 7 · Hand-off. The popup came back and the app is booting behind this. */
export const AuthHandoff = ({ name, mobile = false }) => (
  <Frame mobile={mobile}>
    <AuthPhoto compact={mobile} />
    <Pane mobile={mobile} footer={false}>
      <Eyebrow color={colors.moss} style={{ marginBottom: mobile ? 12 : 14 }}>Signed in</Eyebrow>
      <Title size={mobile ? 26 : 30}>{name ? `You are in, ${name}.` : 'You are in.'}</Title>
      <Body style={{ marginBottom: 0 }}>Opening Home. From here the app takes over and routes you to your default landing page.</Body>
      <div style={{ marginTop: 26, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: colors.gray2 }}>
        <Spinner color={colors.moss} />Loading Workspace
      </div>
    </Pane>
  </Frame>
);

/* The suspense fallback for the token-secured public routes, which render
   outside the app shell and used to show white-on-noir "Loading...". */
export const AuthRouteLoader = ({ label = 'Loading' }) => (
  <div className="reve-auth-shell" style={{ display: 'grid', placeItems: 'center', background: colors.white, fontFamily: fonts.body }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: colors.textSoft }}>
      <Spinner />{label}
    </div>
  </div>
);

/* The first name, for the states that use one. Falls back to the local part of
   the address, which is `first.last` on every reverealtors.com account. */
export const firstNameOf = (user) => {
  const display = (user?.displayName || '').trim();
  if (display) return display.split(/\s+/)[0];
  const local = (user?.email || '').split('@')[0];
  if (!local) return '';
  const first = local.split(/[._-]/)[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
};
